import { Button, buttonVariants, Chip, ErrorBanner, Picker, Textarea, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Check, ChevronRight, Circle, MapPin, Sparkles } from 'lucide-react'
import * as React from 'react'
import { formatWhen } from '../../lib/display-time'
import { describeError, getJson, writeJson } from '../mobile/api'

export type WorkflowKind = 'remote_fix' | 'onsite_visit' | 'macd' | 'callback'

interface WorkflowStep {
  key: string
  label: string
  help: string
  required: boolean
  requires_note: boolean
  done: boolean
  note?: string | null
}

interface WorkflowGate {
  key: string
  label: string
  satisfied: boolean
  detail: string
}

export interface TechWorkflow {
  id: string
  case_id: string
  kind: WorkflowKind
  label: string
  state: 'active' | 'completed' | 'handed_back'
  steps: WorkflowStep[]
  gates: WorkflowGate[]
  missing: string[]
  ready: boolean
  customer_outcome: 'pending' | 'confirmed_fixed' | 'couldnt_reach'
  system_change: 'unknown' | 'not_changed' | 'changed'
  conduit_update: 'not_required' | 'required' | 'updated'
  resolution_note?: string | null
  customer_unreachable_at?: string | null
}

const WORKFLOWS: { id: WorkflowKind; label: string; description: string }[] = [
  { id: 'remote_fix', label: 'Remote fix', description: 'Diagnose, connect, fix and confirm.' },
  { id: 'onsite_visit', label: 'On-site visit', description: 'Check in, photos, work, sign-off and check out.' },
  { id: 'macd', label: 'MACD / phone change', description: 'Make the change, update Conduit and confirm.' },
  { id: 'callback', label: 'Callback / voicemail', description: 'Call, log it and record the outcome.' },
]

export const techWorkflowKey = (caseId: string) => ['case', caseId, 'tech-workflow'] as const

export async function readTechWorkflow(caseId: string): Promise<TechWorkflow | null> {
  return (await getJson<TechWorkflow | null>(`/cases/${encodeURIComponent(caseId)}/tech-workflow`)).data
}

function InlineContext({ summary, site }: { summary?: string | null; site?: string | null }) {
  if (!summary && !site) return null
  return (
    <div className="grid gap-2 text-sm sm:grid-cols-2">
      {summary ? (
        <div className="rounded-lg border border-riley-border bg-riley-soft/40 p-3">
          <p className="mb-1 flex items-center gap-1 font-medium text-riley">
            <Sparkles className="size-4" /> Riley's summary
          </p>
          <p>{summary}</p>
        </div>
      ) : null}
      {site ? (
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="mb-1 flex items-center gap-1 font-medium">
            <MapPin className="size-4" /> Site
          </p>
          <p>{site}</p>
        </div>
      ) : null}
    </div>
  )
}

export function TechWorkflowPanel({
  caseId,
  caseNumber,
  summary,
  site,
  compact = false,
  onOpenDetails,
}: {
  caseId: string
  caseNumber: string
  summary?: string | null
  site?: string | null
  compact?: boolean
  onOpenDetails?: () => void
}) {
  const qc = useQueryClient()
  const query = useQuery({ queryKey: techWorkflowKey(caseId), queryFn: () => readTechWorkflow(caseId), retry: false })
  const workflow = query.data
  const [notes, setNotes] = React.useState<Record<string, string>>({})
  const [reason, setReason] = React.useState('')
  const [details, setDetails] = React.useState('')

  const mutation = useMutation({
    mutationFn: (call: () => Promise<TechWorkflow | null>) => call(),
    onSuccess: (saved) => {
      qc.setQueryData(techWorkflowKey(caseId), saved)
      void qc.invalidateQueries({ queryKey: ['case-update-form', caseId] })
    },
  })
  const run = (call: () => Promise<TechWorkflow | null>, message?: string) =>
    mutation.mutate(call, { onSuccess: () => message && toast.success(message) })
  const patchRequirements = (body: Record<string, unknown>) =>
    writeJson<TechWorkflow>('PATCH', `/cases/${encodeURIComponent(caseId)}/tech-workflow/requirements`, body)

  if (query.isError) {
    return <ErrorBanner title="Guided workflow is unreachable" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
  }
  if (query.isPending) return <p className="p-3 text-sm text-muted-foreground">Loading guided workflow…</p>
  if (!workflow) {
    return (
      <section className={compact ? 'space-y-3' : 'mx-4 my-3 space-y-3 rounded-xl border border-primary/25 bg-primary-soft/30 p-4'}>
        <div>
          <h2 className="font-semibold">Choose the work you’re doing</h2>
          <p className="text-sm text-muted-foreground">Patchbay will keep the steps and close requirements in view.</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {WORKFLOWS.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={mutation.isPending}
              onClick={() =>
                run(
                  () => writeJson<TechWorkflow>('PUT', `/cases/${encodeURIComponent(caseId)}/tech-workflow`, { kind: item.id }),
                  `${item.label} started`,
                )
              }
              className="flex min-h-16 cursor-pointer items-center gap-2 rounded-lg border border-border bg-card p-3 text-left hover:border-primary disabled:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{item.label}</span>
                <span className="block text-xs text-muted-foreground">{item.description}</span>
              </span>
              <ChevronRight className="size-4" />
            </button>
          ))}
        </div>
        {mutation.error ? <p className="text-sm text-destructive">{describeError(mutation.error)}</p> : null}
      </section>
    )
  }

  return (
    <section
      aria-label={`${workflow.label} workflow`}
      className={compact ? 'space-y-4' : 'mx-4 my-3 space-y-4 rounded-xl border border-primary/25 bg-primary-soft/20 p-4'}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium tracking-wide text-primary uppercase">Guided work · {caseNumber}</p>
          <h2 className="text-lg font-semibold">{workflow.label}</h2>
        </div>
        <Chip tone={workflow.ready ? 'success' : 'warning'}>
          {workflow.ready ? 'Ready to close or hand back' : `${workflow.missing.length} missing`}
        </Chip>
      </div>

      {!compact ? <InlineContext summary={summary} site={site} /> : null}
      <p className="text-xs text-muted-foreground">
        System, contacts, history and secure credential reveal stay in Details.{' '}
        {onOpenDetails ? (
          <button type="button" className="font-medium text-primary underline" onClick={onOpenDetails}>
            Open Details
          </button>
        ) : (
          <Link to="/case/$id" params={{ id: caseNumber }} className="font-medium text-primary underline">
            Open the full case
          </Link>
        )}
      </p>

      <ol className="space-y-2" aria-label="Workflow steps">
        {workflow.steps.map((step, index) => {
          const note = notes[step.key] ?? step.note ?? ''
          return (
            <li key={step.key} className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                  {step.done ? <Check className="size-4 text-success" /> : index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {step.label} {!step.required ? <span className="text-xs font-normal text-muted-foreground">when needed</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.help}</p>
                  {step.requires_note && !step.done ? (
                    <Textarea
                      className="mt-2 min-h-16"
                      aria-label={`${step.label} note`}
                      placeholder="What did you find or do?"
                      value={note}
                      onChange={(event) => setNotes((current) => ({ ...current, [step.key]: event.target.value }))}
                    />
                  ) : step.note ? (
                    <p className="mt-2 rounded bg-muted px-2 py-1 text-sm">{step.note}</p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant={step.done ? 'ghost' : 'outline'}
                  disabled={mutation.isPending || (!step.done && step.requires_note && note.trim().length < 3)}
                  disabledReason={mutation.isPending ? 'Saving…' : 'Write at least 3 characters in the note'}
                  onClick={() =>
                    run(() =>
                      writeJson<TechWorkflow>(
                        'PATCH',
                        `/cases/${encodeURIComponent(caseId)}/tech-workflow/steps/${encodeURIComponent(step.key)}`,
                        { done: !step.done, note: note.trim() || null },
                      ),
                    )
                  }
                >
                  {step.done ? 'Undo' : 'Done'}
                </Button>
              </div>
            </li>
          )
        })}
      </ol>

      <div className="space-y-3 rounded-lg border border-border bg-card p-3">
        <h3 className="font-semibold">Required before close or hand-back</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1 text-sm font-medium">
            <span>Customer outcome</span>
            <Picker
              ariaLabel="Customer outcome"
              className="min-h-10 font-normal"
              value={workflow.customer_outcome}
              disabled={mutation.isPending}
              options={[
                { value: 'pending', label: 'Not confirmed yet' },
                { value: 'confirmed_fixed', label: 'Customer confirmed fixed' },
                { value: 'couldnt_reach', label: 'Couldn’t reach customer' },
              ]}
              onValueChange={(customerOutcome) => run(() => patchRequirements({ customer_outcome: customerOutcome }))}
            />
          </div>
          <div className="space-y-1 text-sm font-medium">
            <span>Did this change the system?</span>
            <Picker
              ariaLabel="System changed"
              className="min-h-10 font-normal"
              value={workflow.system_change}
              disabled={mutation.isPending || workflow.kind === 'macd' || workflow.kind === 'callback'}
              options={[
                { value: 'unknown', label: 'Choose…' },
                { value: 'not_changed', label: 'No system change' },
                { value: 'changed', label: 'System changed' },
              ]}
              onValueChange={(systemChange) => run(() => patchRequirements({ system_change: systemChange }))}
            />
          </div>
        </div>
        {workflow.system_change === 'changed' ? (
          <Button
            variant={workflow.conduit_update === 'updated' ? 'ghost' : 'outline'}
            disabled={mutation.isPending}
            disabledReason="Saving…"
            onClick={() => run(() => patchRequirements({ conduit_updated: workflow.conduit_update !== 'updated' }))}
          >
            {workflow.conduit_update === 'updated' ? <Check /> : <Circle />} Conduit{' '}
            {workflow.conduit_update === 'updated' ? 'updated' : 'still needs updating'}
          </Button>
        ) : null}
        {workflow.customer_outcome === 'couldnt_reach' && workflow.customer_unreachable_at ? (
          <p className="text-xs text-warning">
            Stale-close timer started {formatWhen(workflow.customer_unreachable_at, { style: 'absolute' })}.
          </p>
        ) : null}
        {/* #746 1.2: the resolution is written once, in the Close dialog (ResolutionField), never here. */}
        <div className="space-y-1 text-sm">
          <p className="font-medium">Customer-facing resolution</p>
          {workflow.resolution_note ? (
            <p className="whitespace-pre-wrap rounded-md border bg-muted/40 p-2">{workflow.resolution_note}</p>
          ) : null}
          <Link to="/case/$id" params={{ id: caseNumber }} search={{ action: 'close' }} className={buttonVariants({ variant: 'outline' })}>
            {workflow.resolution_note ? 'Review it and close…' : 'Write the resolution and close…'}
          </Link>
        </div>
        <ul className="space-y-1 text-sm" aria-label="Close requirements">
          {workflow.gates.map((gate) => (
            <li key={gate.key} className="flex items-start gap-2">
              {gate.satisfied ? (
                <Check className="mt-0.5 size-4 shrink-0 text-success" />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-warning" />
              )}
              <span>
                {gate.label}
                {gate.detail ? <span className="block text-xs text-muted-foreground">{gate.detail}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <details className="rounded-lg border border-border bg-card p-3">
        <summary className="cursor-pointer font-medium">Needs dispatch → Clayton</summary>
        <div className="mt-3 space-y-2">
          <input
            aria-label="Dispatch reason"
            className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
            placeholder="Why dispatch is needed"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Textarea
            aria-label="Dispatch details"
            placeholder="What Clayton needs to know"
            value={details}
            onChange={(event) => setDetails(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={mutation.isPending || reason.trim().length < 3 || details.trim().length < 3}
              disabledReason={
                mutation.isPending
                  ? 'Saving…'
                  : reason.trim().length < 3
                    ? 'Write at least 3 characters for the reason'
                    : 'Write at least 3 characters in the details'
              }
              onClick={() =>
                run(
                  () =>
                    writeJson<TechWorkflow | null>('POST', `/cases/${encodeURIComponent(caseId)}/needs-dispatch`, {
                      reason: reason.trim(),
                      details: details.trim(),
                    }),
                  'Clayton was notified',
                )
              }
            >
              Needs dispatch
            </Button>
            <Button
              disabled={!workflow.ready || mutation.isPending || reason.trim().length < 3 || details.trim().length < 3}
              disabledReason={
                mutation.isPending
                  ? 'Saving…'
                  : !workflow.ready
                    ? `Complete first: ${workflow.missing.join('; ')}`
                    : reason.trim().length < 3
                      ? 'Write at least 3 characters for the reason'
                      : 'Write at least 3 characters in the details'
              }
              onClick={() =>
                run(
                  () =>
                    writeJson<TechWorkflow>('POST', `/cases/${encodeURIComponent(caseId)}/tech-workflow/hand-back`, {
                      reason: reason.trim(),
                      details: details.trim(),
                    }),
                  'Case handed back to Clayton',
                )
              }
            >
              Hand back complete work
            </Button>
          </div>
          {!workflow.ready ? <p className="text-xs text-warning">Hand-back is disabled: {workflow.missing.join('; ')}.</p> : null}
        </div>
      </details>
      {mutation.error ? (
        <p role="alert" className="text-sm text-destructive">
          {describeError(mutation.error)}
        </p>
      ) : null}
    </section>
  )
}
